
import React, { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import { selectNotifications, selectNotificationNext, selectNotificationLoading, selectUnreadCount } from '../../modules/notifications/selectors';
import { fetchNotifications, markNotificationAsRead, deleteNotification, markAllNotificationsAsRead } from '../../modules/notifications/actions';
import { INotification as ReduxNotification } from '../../modules/notifications/types';
import { AppDispatch } from '../../store';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import AlertPopup from '../../components/common/AlertPopup';
import {
  PageContainer,
  PageTitle,
  NotificationCard,
  CardHeader,
  NotificationTitle,
  NotificationTime,
  NotificationBody,
  NotificationFooter,
  PageHeader,
  MarkAllReadLink,
  DeleteButtonContainer
} from './styles';

const NotificationPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useSelector(selectNotifications);
  const next = useSelector(selectNotificationNext);
  const loading = useSelector(selectNotificationLoading);
  const unreadCount = useSelector(selectUnreadCount);
  const [page, setPage] = React.useState(1);
  const observerTarget = React.useRef(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = React.useState<number | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [toast, setToast] = React.useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    // Initial fetch
    if (page === 1) {
      dispatch(fetchNotifications({ page: 1, page_size: 10 }));
    }
  }, [dispatch, page]); // Only on mount/initial load. Scroll observer will handle pagination.

  useEffect(() => {
    const currentTarget = observerTarget.current;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && next && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          dispatch(fetchNotifications({ page: nextPage, page_size: 10 }));
        }
      },
      { threshold: 1.0 }
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [next, loading, page, dispatch]);

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'shift_assignment':
      case 'shift_accepted':
      case 'success':
        return theme.palette.primary.main; // Green
      case 'worker_no_show':
      case 'warning':
        return theme.palette.warning.main; // Yellow/Orange
      case 'facility_onboarding':
      case 'info':
        return '#2196F3'; // Blue
      case 'shift_cancellation':
      case 'error':
        return theme.palette.error.main; // Red
      default:
        return theme.palette.grey[500];
    }
  };

  const handleMarkAsRead = (notification: ReduxNotification) => {
    if (!notification.is_read) {
      dispatch(markNotificationAsRead(notification.notification_id))
        .unwrap()
        .then(() => {
          // Reducer already updates the locally in the list
        });
    }
  };

  const handleDeleteClick = (event: React.MouseEvent, notificationId: number) => {
    event.stopPropagation();
    setSelectedNotificationId(notificationId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedNotificationId) {
      setIsDeleting(true);
      dispatch(deleteNotification(selectedNotificationId))
        .unwrap()
        .then(() => {
          setIsDeleting(false);
          setDeleteConfirmOpen(false);
          setToast({ open: true, message: 'Notification deleted successfully!', severity: 'success' });
          // Reducer handles local removal
        })
        .catch((err) => {
          setIsDeleting(false);
          setToast({ open: true, message: err || 'Failed to delete notification', severity: 'error' });
        });
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsAsRead())
      .unwrap()
      .then(() => {
        // Reducer updates all to read
      })
      .catch((err: any) => {
        console.error('Failed to mark all notifications as read:', err);
      });
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle variant="h4">Notifications</PageTitle>
        {unreadCount > 0 && (
          <MarkAllReadLink onClick={handleMarkAllRead}>
            Mark all as read
          </MarkAllReadLink>
        )}
      </PageHeader>
      
      {notifications.map((notification: ReduxNotification) => (
        <NotificationCard
          key={notification.notification_id}
          statusColor={getStatusColor(notification.notification_type)}
          isRead={notification.is_read}
          onClick={() => handleMarkAsRead(notification)}
          style={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}
        >
          <div style={{ flex: 1 }}>
            <CardHeader>
              <NotificationTitle>{notification.notification_title}</NotificationTitle>
              <NotificationTime>{new Date(notification.created_on).toLocaleString()}</NotificationTime>
            </CardHeader>
            <NotificationBody>
              {notification.notification_message}
            </NotificationBody>
            <NotificationFooter>
              {notification.send_by ? `Sent by: ${notification.send_by.first_name} ${notification.send_by.last_name}` : ''}
            </NotificationFooter>
          </div>
          <DeleteButtonContainer onClick={(e) => handleDeleteClick(e, notification.notification_id)}>
            <DeleteIcon sx={{ fontSize: '1.5rem' }} />
          </DeleteButtonContainer>
        </NotificationCard>
      ))}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Notification"
        message="Are you sure you want to delete this notification?"
        loading={isDeleting}
      />

      <AlertPopup
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
      {notifications.length === 0 && !loading && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
          No notifications found.
        </div>
      )}
      <div ref={observerTarget} style={{ height: '20px', margin: '20px 0', textAlign: 'center' }}>
        {loading && 'Loading more notifications...'}
      </div>
    </PageContainer>
  );
};

export default NotificationPage;
